import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../utils/supabase';
import { createTournament, TOURNAMENT_NAME_MAX_LENGTH } from '../services/tournamentService';
import { getCurrentSeasonId } from '../utils/seasonUtils';
import { Button, BackButton } from '../components/common';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { FormField, Input, Select } from '../components/common/Form';
import { DEFAULT_SCORING } from '../utils/gameScoreRules';
import {
  DEFAULT_SESSION_START_TIME,
  DEFAULT_SESSION_END_TIME,
} from '../utils/sessionTimeUtils';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';

const CREATE_TOURNAMENT_PAGE_SHELL = `${APP_PAGE_BLEED_ROOT} min-h-screen bg-background`;
const CREATE_TOURNAMENT_PAGE_INNER = `mx-auto max-w-3xl ${APP_PAGE_GUTTER} pt-3 pb-8 md:pt-6`;

const BEST_OF_OPTIONS = [
  { value: '1', label: 'Best of 1' },
  { value: '3', label: 'Best of 3' },
  { value: '5', label: 'Best of 5' },
];

const CreateTournamentPage = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [tournamentDate, setTournamentDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState(DEFAULT_SESSION_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_SESSION_END_TIME);
  const [location, setLocation] = useState('');
  const [gameTypeId, setGameTypeId] = useState('1');
  const [numberOfCourts, setNumberOfCourts] = useState('2');
  const [defaultBestOf, setDefaultBestOf] = useState('1');
  const [pointsTarget, setPointsTarget] = useState(String(DEFAULT_SCORING.pointsTarget));
  const [scoreValidation, setScoreValidation] = useState(DEFAULT_SCORING.deuceMode);
  const [seasonId, setSeasonId] = useState(null);

  const [gameTypes, setGameTypes] = useState({ doubles: [], singles: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGameTypes();
    fetchSeasonId();
  }, [clubId]);

  const fetchGameTypes = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('game_types')
        .select('*')
        .in('id', [1, 2, 3, 4, 5, 6, 7]);

      if (fetchError) throw fetchError;

      const rows = (data || []).map((gt) => {
        if (gt.id === 6) return { ...gt, name: 'Open Doubles' };
        if (gt.id === 7) return { ...gt, name: 'Open Singles' };
        return gt;
      });

      const doubles = rows
        .filter((gt) => gt.is_doubles !== false)
        .sort((a, b) => {
          if (a.id === 6) return 1;
          if (b.id === 6) return -1;
          return a.id - b.id;
        });
      const singles = rows
        .filter((gt) => gt.is_doubles === false)
        .sort((a, b) => a.id - b.id);

      setGameTypes({ doubles, singles });
    } catch (err) {
      console.error('Error fetching game types:', err);
    }
  };

  const fetchSeasonId = async () => {
    try {
      const id = await getCurrentSeasonId(clubId);
      setSeasonId(id);
    } catch (err) {
      console.error('Error fetching season:', err);
    }
  };

  const gameTypeOptions = [
    ...(gameTypes.doubles || []).map((gt) => ({ value: String(gt.id), label: gt.name })),
    ...(gameTypes.singles || []).map((gt) => ({ value: String(gt.id), label: gt.name })),
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError('Tournament name is required');
      return;
    }
    if (trimmedName.length > TOURNAMENT_NAME_MAX_LENGTH) {
      setError(`Tournament name must be ${TOURNAMENT_NAME_MAX_LENGTH} characters or fewer`);
      return;
    }
    if (!tournamentDate) {
      setError('Date is required');
      return;
    }
    if (!startTime) {
      setError('Start time is required');
      return;
    }
    if (endTime && endTime <= startTime) {
      setError('End time must be after start time');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const tournament = await createTournament(clubId, {
        name: trimmedName,
        gameTypeId: parseInt(gameTypeId, 10),
        defaultBestOf: parseInt(defaultBestOf, 10),
        numberOfCourts: parseInt(numberOfCourts, 10),
        pointsTarget: parseInt(pointsTarget, 10),
        scoreValidation,
        tournamentDate,
        startTime,
        endTime: endTime || null,
        location,
        seasonId,
        createdBy: currentUser.id,
      });

      navigate(`/club/${clubId}/tournaments/${tournament.id}`);
    } catch (err) {
      console.error('Error creating tournament:', err);
      setError(err.message || 'Failed to create tournament');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={CREATE_TOURNAMENT_PAGE_SHELL}>
      <div className={CREATE_TOURNAMENT_PAGE_INNER}>
        <div className="mb-6 sm:mb-8">
          <BackButton hideOnDesktop className="mb-2" onClick={() => navigate(`/club/${clubId}/tournaments`)}>
            Back to Tournaments
          </BackButton>
          <Heading level={1} className="mb-1 text-xl font-bold text-text-primary sm:text-2xl">
            Create Tournament
          </Heading>
          <Text className="text-sm text-text-secondary">Set up a new badminton tournament</Text>
        </div>

        {error && (
          <Card variant="danger" size="small" className="mb-6">
            <Typography variant="body">{error}</Typography>
          </Card>
        )}

        <Card variant="default" size="large">
          <form onSubmit={handleSubmit} className="space-y-6">
            <FormField label="Tournament name" required>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Spring Championship 2026"
                maxLength={TOURNAMENT_NAME_MAX_LENGTH}
                required
              />
            </FormField>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormField label="Date" required>
                <Input
                  type="date"
                  value={tournamentDate}
                  onChange={(e) => setTournamentDate(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Start time" required>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="End time">
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </FormField>
            </div>

            <FormField label="Location">
              <Input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Venue or court"
              />
            </FormField>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Game type" required>
                <Select
                  value={gameTypeId}
                  onChange={(e) => setGameTypeId(e.target.value)}
                  options={gameTypeOptions}
                  required
                />
              </FormField>
              <FormField label="Courts" required>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={numberOfCourts}
                  onChange={(e) => setNumberOfCourts(e.target.value)}
                  required
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormField label="Match format" required>
                <Select
                  value={defaultBestOf}
                  onChange={(e) => setDefaultBestOf(e.target.value)}
                  options={BEST_OF_OPTIONS}
                  required
                />
              </FormField>
              <FormField label="Points to win">
                <Select
                  value={pointsTarget}
                  onChange={(e) => setPointsTarget(e.target.value)}
                  options={[
                    { value: '21', label: '21' },
                    { value: '15', label: '15' },
                  ]}
                />
              </FormField>
              <FormField label="Deuce">
                <Select
                  value={scoreValidation}
                  onChange={(e) => setScoreValidation(e.target.value)}
                  options={[
                    { value: 'standard', label: 'Standard' },
                    { value: 'no_deuce', label: 'No deuce' },
                  ]}
                />
              </FormField>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !name.trim() || !tournamentDate || !startTime}
              >
                {loading ? 'Creating...' : 'Create Tournament'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/club/${clubId}/tournaments`)}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CreateTournamentPage;
